import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Wand2, Sparkles, Trash2, Plus, CheckCircle2, Circle, GripVertical } from 'lucide-react';
import * as C from '../constants';
import api, { getToken } from '../api';
import AlertToast from './AlertToast';
import { sha256Hex } from '../utils/browserCompat'; // http 站点没有 crypto.subtle，去重要能降级

/**
 * StoryboardMasterModal — 分镜大师「能力」全局设定（超管）
 *
 * 与"用哪个模型当引擎"解耦：这里配的是分镜大师这个能力本身，所有模型共用一套。
 *   - 触发按钮图标：画布上点开模型选择上拉框的那个按钮的图标
 *   - 分镜大师 Skills（system_prompt）：润色时作为系统提示词，所有模型共用
 *   - 分镜草图提示词（base_prompt）：生分镜草图图片时附加在最前
 *   - 画面风格预设（style_presets）：生图时可选，附加在草图提示词之上
 *
 * 模型本身（引擎：模型名/Key/温度/单价）在「API 设置」里单独配，每配一个就在画布上拉框多一个选项。
 *
 * 存储：全部走 settings 表，读写 /api/admin/storyboard-settings。
 */

const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 4100 };
const modalStyle = { width: 760, maxWidth: '94vw', height: '86vh', background: 'var(--modal-surface)', border: '1px solid var(--border-strong)', borderRadius: 12, color: 'var(--text-body)', boxShadow: 'var(--shadow-panel)', display: 'flex', flexDirection: 'column' };
const header = { padding: '14px 16px', borderBottom: '1px solid var(--border-subtle)', fontWeight: 700, fontSize: 14, flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--modal-input-bg)', color: 'var(--text-primary)', outline: 'none', fontSize: 13, boxSizing: 'border-box' };
const labelStyle = { fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6, display: 'block' };
const btn = (primary) => ({ padding: '8px 16px', borderRadius: 8, border: '1px solid ' + (primary ? '#3b82f6' : 'var(--border-strong)'), background: primary ? '#3b82f6' : 'transparent', color: primary ? '#fff' : 'var(--text-body)', cursor: 'pointer', fontSize: 13 });

export default function StoryboardMasterModal({ open, onClose }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [triggerIcon, setTriggerIcon] = useState('');
  const [fillIcon, setFillIcon] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [basePrompt, setBasePrompt] = useState('');
  const [stylePresets, setStylePresets] = useState([]);  // [{ id, name, logoUrl, prompt }]
  const [modelIds, setModelIds] = useState([]);           // 勾选的语言模型 DB id
  const [languageModels, setLanguageModels] = useState([]); // 共享池
  const [dragId, setDragId] = useState(null);             // 已选模型拖动排序中的项
  const [iconUploading, setIconUploading] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');

  const showToast = (msg, type = 'success', duration = 3500) => {
    setToastMessage(msg); setToastType(type);
    setTimeout(() => setToastMessage(''), duration);
  };

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get('/api/admin/storyboard-settings').then(data => {
      setTriggerIcon(data?.triggerIcon || '');
      setFillIcon(data?.fillIcon || '');
      setSystemPrompt(data?.systemPrompt || '');
      setBasePrompt(data?.basePrompt || '');
      setStylePresets(Array.isArray(data?.stylePresets) ? data.stylePresets : []);
      setModelIds(Array.isArray(data?.modelIds) ? data.modelIds : []);
      setLanguageModels(Array.isArray(data?.languageModels) ? data.languageModels : []);
    }).catch(e => {
      showToast('❌ 加载分镜大师设定失败：' + (e.message || '未知错误'), 'warning');
    }).finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;

  // 上传图片，返回 URL（带 hash 去重，与其他上传一致）
  const uploadImage = async (file) => {
    try {
      const buffer = await file.arrayBuffer();
      // 拿不到哈希（http 非安全上下文）就跳过去重预检，直接上传
      const hashHex = await sha256Hex(buffer);
      if (hashHex) {
        const checkResult = await api.get(`/api/upload/check-hash?hash=${hashHex}&type=logo`);
        if (checkResult.exists && checkResult.url) return checkResult.url;
      }
      const formData = new FormData(); formData.append('file', file);
      const result = await fetch(`${C.API_BASE_URL}/api/upload?type=logo`, { method: 'POST', headers: { 'Authorization': `Bearer ${getToken()}` }, body: formData }).then(r => r.json());
      if (result.url) return result.url;
      if (result.error) showToast('❌ 上传失败: ' + result.error, 'warning');
    } catch (err) {
      console.error('上传失败:', err);
      showToast('❌ 上传失败', 'warning');
    }
    return '';
  };

  // 画面风格预设：增/改/删
  const addPreset = () => setStylePresets(list => [...list, { id: `sp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, name: '', logoUrl: '', prompt: '' }]);
  const toggleModel = (id) => setModelIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  // 已选模型拖动排序：顺序即画布分镜大师上拉框出现顺序
  const reorderTo = (targetId) => {
    setModelIds(prev => {
      if (dragId == null || dragId === targetId) return prev;
      const arr = [...prev];
      const from = arr.indexOf(dragId);
      const to = arr.indexOf(targetId);
      if (from < 0 || to < 0) return prev;
      arr.splice(to, 0, arr.splice(from, 1)[0]);
      return arr;
    });
  };
  const updatePreset = (idx, patch) => setStylePresets(list => list.map((it, i) => i === idx ? { ...it, ...patch } : it));
  const removePreset = (idx) => setStylePresets(list => list.filter((_, i) => i !== idx));

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = await api.post('/api/admin/storyboard-settings', {
        triggerIcon, fillIcon, systemPrompt, basePrompt, stylePresets, modelIds,
      });
      setTriggerIcon(data?.triggerIcon || '');
      setFillIcon(data?.fillIcon || '');
      setSystemPrompt(data?.systemPrompt || '');
      setBasePrompt(data?.basePrompt || '');
      setStylePresets(Array.isArray(data?.stylePresets) ? data.stylePresets : []);
      setModelIds(Array.isArray(data?.modelIds) ? data.modelIds : []);
      setLanguageModels(Array.isArray(data?.languageModels) ? data.languageModels : []);
      window.dispatchEvent(new Event('modelsUpdated'));  // 通知画布刷新工具箱配置（图标/风格）
      showToast('✅ 已保存分镜大师设定', 'success', 2500);
    } catch (e) {
      showToast('❌ 保存失败：' + (e.message || '未知错误'), 'warning');
    } finally {
      setSaving(false);
    }
  };

  const modalContent = (
    <div style={overlay} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modalStyle} onMouseDown={(e) => e.stopPropagation()}>
        <div style={header}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Wand2 size={16} color="#60a5fa" /> 分镜大师设定</span>
          <X size={18} style={{ cursor: 'pointer' }} onClick={onClose} />
        </div>

        <div className="custom-scrollbar" style={{ flex: 1, padding: 20, overflowY: 'auto', minHeight: 0 }}>
          {loading ? (
            <div style={{ fontSize: 12, color: '#475569', textAlign: 'center', padding: '40px 0' }}>加载中...</div>
          ) : (
            <>
              <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.6, marginBottom: 18, padding: '8px 12px', background: 'rgba(96,165,250,0.06)', border: '1px solid rgba(96,165,250,0.18)', borderRadius: 8 }}>
                这里配的是分镜大师「能力」本身，所有模型共用。具体用哪个模型（豆包 / Claude）当引擎，在「已添加模型」里添加语言模型后，到下面「选择模型」勾选 —— 勾了哪些，画布分镜大师上拉框里就出现哪些。
              </div>

              {/* 选择模型 */}
              <div style={{ marginBottom: 20 }}>
                <label style={labelStyle}>选择模型（勾选后在画布分镜大师上拉框出现；拖动已选项调整顺序 = 上拉框出现顺序）</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: 12, background: 'var(--modal-input-bg)', borderRadius: 8 }}>
                  {languageModels.length === 0 && (
                    <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.6 }}>暂无语言模型。请先在右侧「已添加模型」里添加语言模型（豆包 / Claude / DeepSeek），再回到这里勾选。</div>
                  )}
                  {(() => {
                    const byId = new Map(languageModels.map(m => [m.id, m]));
                    const selected = modelIds.map(id => byId.get(id)).filter(Boolean);
                    const unselected = languageModels.filter(m => !modelIds.includes(m.id));
                    const canDrag = selected.length > 1;
                    const renderRow = (m, isSelected, draggable) => (
                      <div key={m.id}
                        draggable={draggable}
                        onDragStart={() => draggable && setDragId(m.id)}
                        onDragOver={e => { if (draggable && dragId != null) e.preventDefault(); }}
                        onDrop={() => { if (draggable) { reorderTo(m.id); setDragId(null); } }}
                        onDragEnd={() => setDragId(null)}
                        onClick={() => toggleModel(m.id)}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 6, cursor: 'pointer',
                          background: isSelected ? 'rgba(96,165,250,0.12)' : 'transparent', border: `1px solid ${isSelected ? 'rgba(96,165,250,0.3)' : 'transparent'}`,
                          opacity: dragId === m.id ? 0.4 : 1 }}
                        onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                        onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}>
                        {draggable
                          ? <GripVertical size={16} color="#64748b" style={{ flexShrink: 0, cursor: 'grab' }} onClick={e => e.stopPropagation()} />
                          : <span style={{ width: 16, flexShrink: 0 }} />}
                        {isSelected ? <CheckCircle2 size={18} color="#60a5fa" style={{ flexShrink: 0 }} /> : <Circle size={18} color="#6b7280" style={{ flexShrink: 0 }} />}
                        {m.logoUrl ? <img src={m.logoUrl} alt="" style={{ width: 20, height: 20, objectFit: 'contain', borderRadius: 3, flexShrink: 0 }} /> : <Wand2 size={16} color="#60a5fa" style={{ flexShrink: 0 }} />}
                        <span style={{ flex: 1, fontSize: 13, color: 'var(--text-body)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</span>
                        {m.supportsVision && <span style={{ fontSize: 10, color: '#86efac', flexShrink: 0 }}>支持传图</span>}
                        {!m.enabled && <span style={{ fontSize: 10, color: '#f87171', flexShrink: 0 }}>未启用</span>}
                        {m.pricePer1kOutput > 0 && <span style={{ fontSize: 10, color: 'var(--amber-accent)', flexShrink: 0 }}>{m.pricePer1kOutput}/千token</span>}
                      </div>
                    );
                    return (
                      <>
                        {selected.map(m => renderRow(m, true, canDrag))}
                        {unselected.map(m => renderRow(m, false, false))}
                      </>
                    );
                  })()}
                </div>
                <div style={{ fontSize: 10, color: '#64748b', marginTop: 6 }}>分镜大师需要传参考图时，请勾选标「支持传图」的模型（如豆包 / Claude）。</div>
              </div>

              {/* 触发按钮图标 */}
              <div style={{ marginBottom: 20 }}>
                <label style={labelStyle}>分镜大师按钮图标（可选）</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 48, height: 48, borderRadius: 8, background: 'var(--modal-input-bg)', border: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                    {triggerIcon ? <img src={triggerIcon} style={{ width: '100%', height: '100%', objectFit: 'contain' }} alt="" /> : <Wand2 size={20} style={{ color: '#60a5fa', opacity: 0.7 }} />}
                  </div>
                  <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" style={{ display: 'none' }} id="sb-trigger-icon"
                    onChange={async (e) => { const f = e.target.files?.[0]; e.target.value = ''; if (!f) return; setIconUploading(true); const url = await uploadImage(f); if (url) setTriggerIcon(url); setIconUploading(false); }} />
                  <label htmlFor="sb-trigger-icon" style={{ padding: '8px 16px', background: iconUploading ? 'rgba(59,130,246,0.3)' : 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 6, color: '#60a5fa', fontSize: 13, cursor: iconUploading ? 'not-allowed' : 'pointer', userSelect: 'none' }}>
                    {iconUploading ? '上传中...' : '上传图标'}
                  </label>
                  {triggerIcon && <button onClick={() => setTriggerIcon('')} style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, color: '#f87171', fontSize: 13, cursor: 'pointer' }}>清除</button>}
                </div>
                <div style={{ fontSize: 10, color: '#64748b', marginTop: 4 }}>画布上点开模型选择框的那个按钮的图标。留空则用内置魔棒图标。所有模型共用这一个按钮。</div>
              </div>

              {/* 填充按钮图标 */}
              <div style={{ marginBottom: 20 }}>
                <label style={labelStyle}>分镜填充按钮图标（可选）</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 48, height: 48, borderRadius: 8, background: 'var(--modal-input-bg)', border: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                    {fillIcon ? <img src={fillIcon} style={{ width: '100%', height: '100%', objectFit: 'contain' }} alt="" /> : <Sparkles size={20} style={{ color: '#86efac', opacity: 0.7 }} />}
                  </div>
                  <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" style={{ display: 'none' }} id="sb-fill-icon"
                    onChange={async (e) => { const f = e.target.files?.[0]; e.target.value = ''; if (!f) return; setIconUploading(true); const url = await uploadImage(f); if (url) setFillIcon(url); setIconUploading(false); }} />
                  <label htmlFor="sb-fill-icon" style={{ padding: '8px 16px', background: iconUploading ? 'rgba(59,130,246,0.3)' : 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 6, color: '#60a5fa', fontSize: 13, cursor: iconUploading ? 'not-allowed' : 'pointer', userSelect: 'none' }}>
                    {iconUploading ? '上传中...' : '上传图标'}
                  </label>
                  {fillIcon && <button onClick={() => setFillIcon('')} style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, color: '#f87171', fontSize: 13, cursor: 'pointer' }}>清除</button>}
                </div>
                <div style={{ fontSize: 10, color: '#64748b', marginTop: 4 }}>分镜节点右侧文本框里那个「填充」按钮的图标。留空则用内置星形图标。</div>
              </div>

              {/* 分镜大师 Skills */}
              <div style={{ marginBottom: 20 }}>
                <label style={labelStyle}>分镜大师 Skills（Agent 设定，所有模型共用）</label>
                <textarea style={{ ...inputStyle, minHeight: 140, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }} value={systemPrompt} onChange={e => setSystemPrompt(e.target.value)}
                  placeholder="输入系统提示词，告诉模型如何把原始脚本格式化为标准分镜…" />
              </div>

              {/* 分镜草图提示词 */}
              <div style={{ marginBottom: 20 }}>
                <label style={labelStyle}>分镜草图提示词（生图时附加到分镜内容前）</label>
                <textarea style={{ ...inputStyle, minHeight: 100, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }} value={basePrompt} onChange={e => setBasePrompt(e.target.value)}
                  placeholder="输入分镜草图提示词，指导生图模型按分镜大师风格生成…" />
              </div>

              {/* 画面风格预设 */}
              <div style={{ marginBottom: 10 }}>
                <label style={labelStyle}>画面风格预设（生图时可选，附加在分镜草图提示词之上）</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 12, background: 'var(--modal-input-bg)', borderRadius: 8 }}>
                  {stylePresets.length === 0 && (
                    <div style={{ fontSize: 11, color: '#64748b' }}>暂无画面风格，点下方按钮新增。新增后可在分镜节点控制台下拉选择。</div>
                  )}
                  {stylePresets.map((item, idx) => (
                    <div key={item.id || idx} style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 10, background: 'var(--modal-input-bg)', border: '1px solid var(--border-subtle)', borderRadius: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 40, height: 40, borderRadius: 8, background: 'var(--modal-input-bg)', border: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                          {item.logoUrl ? <img src={item.logoUrl} style={{ width: '100%', height: '100%', objectFit: 'contain' }} alt="" onError={e => { e.target.style.display = 'none'; }} /> : <Sparkles size={18} style={{ color: '#64748b', opacity: 0.5 }} />}
                        </div>
                        <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" style={{ display: 'none' }} id={`sb-style-logo-${item.id || idx}`}
                          onChange={async (e) => { const f = e.target.files?.[0]; e.target.value = ''; if (!f) return; const url = await uploadImage(f); if (url) updatePreset(idx, { logoUrl: url }); }} />
                        <label htmlFor={`sb-style-logo-${item.id || idx}`} style={{ padding: '6px 12px', background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 6, color: '#60a5fa', fontSize: 12, cursor: 'pointer', userSelect: 'none', flexShrink: 0 }}>上传图标</label>
                        <input style={{ ...inputStyle, flex: 1 }} value={item.name || ''} placeholder="风格名称（如：电影感、赛博朋克）" onChange={e => updatePreset(idx, { name: e.target.value })} />
                        <button onClick={() => removePreset(idx)} style={{ padding: '6px 8px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, color: '#f87171', cursor: 'pointer', flexShrink: 0 }}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                      <textarea style={{ ...inputStyle, minHeight: 70, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }} value={item.prompt || ''} placeholder="该风格的附加提示词，生图时拼在最前层…" onChange={e => updatePreset(idx, { prompt: e.target.value })} />
                    </div>
                  ))}
                  <button onClick={addPreset} style={{ ...btn(false), display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, color: '#60a5fa', borderColor: 'rgba(59,130,246,0.3)' }}>
                    <Plus size={14} /> 新增画面风格
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        <div style={{ padding: '14px 20px', borderTop: '1px solid var(--border-subtle)', flexShrink: 0, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={btn(false)}>关闭</button>
          <button onClick={handleSave} disabled={saving || loading} style={{ ...btn(true), opacity: (saving || loading) ? 0.6 : 1 }}>{saving ? '保存中...' : '保存设定'}</button>
        </div>
      </div>

      {toastMessage && <AlertToast message={toastMessage} type={toastType} position="center" />}
    </div>
  );

  return createPortal(modalContent, document.body);
}
